<template functional>
  <component
    :is="props.tag"
    tabindex="0"
    class="event-card flex shadow-1dp rounded cursor-pointer outline-none transition-shadow duration-200
    ease-in-out-quart hover:shadow-4dp any-pointer-fine:focus:shadow-outline"
    @click="listeners['route-navigation']({ action: 'details', eventId: props.event.id })"
    @mousedown.prevent
    @keydown.space.prevent="listeners['route-navigation']({ action: 'details', eventId: props.event.id })"
  >
    <div
      v-if="props.event.startingTime && props.event.startingTime.toDate()"
      :title="`${props.event.title || 'The event'} starts on:
      ${$options.formatFullDateAndTime(props.event.startingTime.toDate())}
      and ends on:
      ${$options.formatFullDateAndTime(props.event.endingTime.toDate())}`"
      class="flex-shrink-0 w-115 flex flex-col justify-between text-primary text-center pt-15 pb-12 px-15 border-r
      border-gray-300 rounded-l"
      :class="[props.ongoing ? 'bg-secondary-light': 'bg-gray-200']"
    >
      <div v-if="props.event.startingTime && props.event.startingTime.toDate()">
        <p class="font-bold text-lg uppercase mb-5">
          {{ $options.formatMonthAndDay(props.event.startingTime.toDate()) }}
        </p>
        <p class="text-sm uppercase mb-5">
          {{ $options.formatWeekDay(props.event.startingTime.toDate()) }}
        </p>
        <p class="text-sm text-primary-light uppercase">
          <FontAwesomeIcon
            :icon="props.ongoing ? ['far', 'hourglass-half'] : ['far', 'clock']"
            class="mr-5"
          />
          {{ $options.formatTime(props.event.startingTime.toDate()) }}
        </p>
      </div>
      <p
        v-if="props.event.packageId && props.pricePackage && props.pricePackage.fields
        && props.pricePackage.fields.shortName"
        class="font-bold text-xs text-primary uppercase tracking-widest mt-20"
      >
        {{ props.pricePackage.fields.shortName }}
      </p>
    </div>
    <div class="w-full min-w-0 flex flex-col justify-between pt-20 pb-7 bg-white rounded-r">
      <div class="px-20">
        <div class="flex justify-between until-mobile-m:w-80 until-mobile-m:flex-wrap">
          <div>
            <h3
              v-if="props.event.title"
              :title="props.event.title"
              class="font-bold text-xl text-primary leading-tight line-clamp-2 mb-3"
            >
              <span class="block capitalize-first-letter">
                {{ props.event.title }}
              </span>
            </h3>
            <p
              v-if="props.event.venue"
              :title="'The event will take place in ' + props.event.venue"
              class="flex leading-snug"
            >
              <FontAwesomeIcon
                :icon="['fas', 'map-marker-alt']"
                class="text-lg text-secondary mr-5"
              />
              <span class="inline-block text-sm text-primary line-clamp-2 pl-4">
                <span class="block text-sm capitalize-first-letter">
                  {{ props.event.venue }}
                </span>
              </span>
            </p>
          </div>
          <img
            v-if="props.event.iconUrl"
            :src="$options.eventIconSrc(props.event.iconUrl)"
            alt=""
            class="max-h-40 min-w-40 ml-20 until-mobile-m:ml-0 until-mobile-m:mt-5"
            @error="$event.target.style.display = 'none'"
          />
        </div>
        <p
          v-if="props.event.description"
          :title="props.event.description"
          class="text-sm text-primary-light leading-tight break-all line-clamp-5 mt-12 p-0 mobile-m:break-normal"
          :class="props.event.description ? 'whitespace-pre-line' : 'italic'"
        >
          <span class="block capitalize-first-letter">
            {{ props.event.description }}
          </span>
        </p>
      </div>
      <div class="flex items-end justify-between mt-20 pl-20 pr-10 mobile-m:flex-row">
        <div class="mb-9 min-w-0">
          <p class="font-medium text-sm text-primary leading-tight">
            <span :title="`${props.event.title || 'The event'} has a limit of ${props.event.maxGuests || 30} guests.`">
              Up to {{ (props.event.maxGuests || 0).toLocaleString() }} guests
            </span>
            <button
              v-if="!props.event.packageId && props.ownedByUser && !props.noUpgrade"
              type="button"
              title="Upgrade this event to allow more guests"
              class="font-bold text-secondary hover:underline focus:outline-none ml-8"
              @click.stop="listeners['route-navigation']({ action: 'upgrade', eventId: props.event.id })"
              @keydown.space.stop
            >
              Need more?
            </button>
          </p>
          <p
            v-if="!props.ownedByUser"
            :title="`${$options.sharedBy(props.event)} shared this event with you`"
            class="flex items-center text-xs text-primary-light leading-tight mt-5"
          >
            <FontAwesomeIcon
              :icon="['fad', 'user-friends']"
              class="flex-shrink-0 text-sm text-secondary mr-6"
            />
            <span class="truncate">Shared by {{ $options.sharedBy(props.event) }}</span>
          </p>
        </div>
        <div class="flex items-end mobile-m:items-center">
          <ElDropdown
            v-if="props.ownedByUser"
            trigger="click"
            :title="`Press here to edit ${props.event.title || 'the event'}`"
            @click.native.stop
          >
            <ElButton
              title="Additional options"
              circle
              class="text-primary border-none"
            >
              <FontAwesomeIcon
                :icon="['far', 'ellipsis-v']"
                class="icon-square-size text-2xl"
              />
            </ElButton>
            <ElDropdownMenu slot="dropdown">
              <ElDropdownItem
                @click.native="listeners['route-navigation']({ action: 'update', eventId: props.event.id })"
              >
                <FontAwesomeIcon
                  fixed-width
                  :icon="['fas', 'pen']"
                  class="text-lg ml-5 mr-15"
                />
                Edit
              </ElDropdownItem>
              <ElDropdownItem
                v-if="!props.noUpgrade"
                @click.native="listeners['route-navigation']({ action: 'upgrade', eventId: props.event.id })"
              >
                <FontAwesomeIcon
                  fixed-width
                  :icon="['fas', 'angle-double-up']"
                  class="text-lg ml-5 mr-15"
                />
                Upgrade
              </ElDropdownItem>
              <ElDropdownItem
                class="text-danger"
                @click.native="listeners['delete-event'](props.event.id)"
              >
                <FontAwesomeIcon
                  fixed-width
                  :icon="['fas', 'trash']"
                  class="text-lg ml-5 mr-15"
                />
                Delete
              </ElDropdownItem>
            </ElDropdownMenu>
          </ElDropdown>
        </div>
      </div>
    </div>
  </component>
</template>

<script>
import formatFullDateAndTime from '@/utils/format/date-time/fullDateAndTime';
import formatMonthAndDay from '@/utils/format/date-time/monthAndDay';
import formatTime from '@/utils/format/date-time/time';
import formatWeekDay from '@/utils/format/date-time/weekDay';

export default {
  props: {
    event: {
      type: Object,
      required: true,
    },
    noUpgrade: {
      type: Boolean,
      default: false,
    },
    ongoing: {
      type: Boolean,
      default: false,
    },
    ownedByUser: {
      type: Boolean,
      default: false,
    },
    pricePackage: {
      type: Object,
      default: null,
    },
    tag: {
      type: String,
      default: 'li',
    },
  },
  // Methods directly in $options
  sharedBy(event) {
    return (event.owner && (event.owner.email || event.owner.name)) || 'another user';
  },
  // `iconUrl` is either a full URL or, as the iOS app reads it, a Firebase Storage path. The only Storage path in
  // use (logos/eventor-logo-rounded.png) is shipped statically in public/, so paths resolve against the app root.
  // Dead links are hidden by the img error handler instead of showing a broken image.
  eventIconSrc(iconUrl) {
    if (/^https?:\/\//i.test(iconUrl)) return iconUrl;

    return `${process.env.BASE_URL}${String(iconUrl).replace(/^\/+/, '')}`;
  },
  formatFullDateAndTime(dateTime) {
    return formatFullDateAndTime(dateTime);
  },
  formatMonthAndDay(dateTime) {
    return formatMonthAndDay(dateTime);
  },
  formatTime(dateTime) {
    return formatTime(dateTime);
  },
  formatWeekDay(dateTime) {
    return formatWeekDay(dateTime);
  },
};
</script>

<style scoped>
</style>
